import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import type { CatalogService } from 'features/catalog/service'
import {
  errorMessage,
  warnYogiQLFallback,
  whenHubFilterList,
} from 'features/charts-v2/utils'
import {
  EMPTY_CATALOG,
  type FeedbackQueryCodec,
} from 'features/feedback/feedback-query'
import { YOGIQL_OFF, type YogiQLFlag } from 'features/feedback/previews-request'
import type { FilterOption } from 'features/filters/types'
import { projectFilterListKey } from 'src/hooks/use-project-filter-list'

/** What the Container hands the filter-hash hook about `enhanced_dialect`:
 *  the flag, the codec once loaded, and a wait for the hub's filter options
 *  on any hub (null: they failed). */
export type CatalogYogiQLPage = {
  flag: YogiQLFlag
  codec: FeedbackQueryCodec | null
  whenHubOptions: () => Promise<FilterOption[] | null>
}

export const YOGIQL_PAGE_OFF: CatalogYogiQLPage = {
  flag: YOGIQL_OFF,
  codec: null,
  whenHubOptions: () => Promise.resolve([]),
}

/**
 * Under `enhanced_dialect`: the codec the page converts v2 filters with (the
 * hub's field catalog and filter options, null until both have settled). A
 * failed catalog leaves custom fields unencodable rather than blocking the page.
 */
export function useCatalogYogiQLPage(
  flag: YogiQLFlag,
  projectId: string,
  catalogService: CatalogService,
): CatalogYogiQLPage {
  const { data: catalog, status } = useQuery(
    ['catalog-yogiql-field-catalog', projectId],
    () => catalogService.getFieldCatalog(projectId),
    {
      enabled: flag.enabled && !!projectId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 2,
      onError: (error) =>
        warnYogiQLFallback(
          `field-catalog request failed, catalog custom-field filters cannot be edited: ${errorMessage(error)}`,
        ),
    },
  )
  // The project root fetches the hub's options; this only watches that query.
  const { data: filterList, status: optionsStatus } = useQuery<FilterOption[]>(
    projectFilterListKey(projectId),
    { enabled: false },
  )

  const catalogSettled = status === 'success' || status === 'error'
  const optionsSettled =
    optionsStatus === 'success' || optionsStatus === 'error'
  const codec = useMemo<FeedbackQueryCodec | null>(
    () =>
      flag.enabled && catalogSettled && optionsSettled
        ? { catalog: catalog ?? EMPTY_CATALOG, options: filterList ?? [] }
        : null,
    [flag.enabled, catalogSettled, optionsSettled, catalog, filterList],
  )
  // Any hub: only a YogiQL key's read waits on the options.
  const queryClient = useQueryClient()
  const whenHubOptions = useCallback(
    () => whenHubFilterList(queryClient, projectId),
    [queryClient, projectId],
  )
  return { flag, codec, whenHubOptions }
}
